import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CommonFunctionsService, DatastoreService } from '../index';

/**
 * External-venue services (global service ids). Add-ons cannot be purchased
 * or viewed for bookings on these services: the Add ons button, the add-ons
 * menu links, and the /add-ons + /optional-add-ons routes are all hidden or
 * blocked.
 */
export const EXTERNAL_VENUE_GLOBAL_SERVICE_IDS: ReadonlySet<number> =
	new Set<number>([
		48395, 48397, 48399, 48413, 48513, 48515, 48517, 48519,
	]);

/**
 * Whether the current booking is for an external-venue service. Matches the
 * booking's service_id or the selected service's global_id (a rescheduled
 * booking's service_id can be the write id, catalogue id + 1), falling back
 * to the localStorage copies so the check still works on a refresh or deep
 * link before the datastore is repopulated.
 */
export function isExternalVenueBooking(dataStore: DatastoreService): boolean {
	const booking = dataStore.booking || readFromStorage('ceremony');
	const service = dataStore.selectedService || readFromStorage('service');
	return (
		EXTERNAL_VENUE_GLOBAL_SERVICE_IDS.has(Number(booking?.service_id)) ||
		EXTERNAL_VENUE_GLOBAL_SERVICE_IDS.has(Number(service?.global_id))
	);
}

function readFromStorage(key: string): any {
	try {
		const raw = localStorage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

/**
 * Blocks the add-ons routes for external-venue bookings so the hidden button
 * and menu links can't be bypassed via URL bar / bookmark / back.
 */
export const externalVenueAddOnsGuard: CanActivateFn = () => {
	const router = inject(Router);
	const dataStore = inject(DatastoreService);
	const commonFunctionsService = inject(CommonFunctionsService);

	if (!isExternalVenueBooking(dataStore)) {
		return true;
	}

	router.navigate([
		`${commonFunctionsService.getCompanyName()}/${commonFunctionsService.getCompanyId()}/our-ceremony`,
	]);
	return false;
};
